// The app-wide "return target" for cross-tab record navigation.
//
// When a user opens a record from an origin that lives in a DIFFERENT tab
// (Overview, Files, Timeline visual, Interrogate deep), the destination is the
// Records tab and the origin tab unmounts. We record where they came from so a
// single shared BackBar can return them there. In-place source panels
// (Financials triage, Interrogate drawer) don't use this — nothing unmounts,
// so their own close IS the way back.
//
// This is the generalization of Financials' pre-existing `summaryFocusRow`
// pattern from "one origin" to "any origin". Financials itself is intentionally
// left on its own state (see CaseView) so its verified restore logic is
// untouched; this module governs the previously-back-control-less origins.
//
// Pure + hook-free so the render gate + label are unit-testable without a DOM.

// Tab ids that a record can be opened FROM and returned TO. Mirrors
// CaseView's Tab union (kept as a string here to avoid a component import).
export interface RecordReturn {
  tab: string;
  // Human label for the origin, e.g. "Overview" — the BackBar shows
  // "← Back to {label}".
  label: string;
}

// The records-tab render gate for the shared record-return BackBar. Shows ONLY
// on the Records destination, ONLY when a cross-tab return is pending, and
// NEVER when Financials' own back affordance is already showing (summaryFocusRow
// set) — so the two mechanisms can't double-render a bar.
export function shouldShowRecordBack(
  tab: string,
  recordReturn: RecordReturn | null,
  summaryFocusRow: unknown,
): boolean {
  return tab === "records" && recordReturn != null && summaryFocusRow == null;
}

// The BackBar label for a pending return — always names the destination.
export function recordBackLabel(recordReturn: RecordReturn): string {
  return `← Back to ${recordReturn.label}`;
}
